import { useEffect, useState, useRef } from 'react';
import { Plus, Edit, Power, Trash2, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { supabase } from '../services/supabase';
import Modal from '../components/ui/Modal';
import Confirm from '../components/ui/Confirm';

// Colaboradores (mig 134) — clon del patrón Patrocinadores SIN descripción. En
// la app del alumno solo se muestra el LOGO; `name` es etiqueta interna.

interface Collaborator {
  id: string;
  name: string;
  logo_url: string;
  url: string;
  sort_order: number;
  active: boolean;
}
interface CollaboratorForm {
  name: string;
  url: string;
  logo_url: string;
  sort_order: number;
  active: boolean;
}
const FORM_EMPTY: CollaboratorForm = {
  name: '',
  url: 'https://',
  logo_url: '',
  sort_order: 0,
  active: true,
};

export default function CollaboratorsPage() {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Collaborator | null>(null);
  const [form, setForm] = useState<CollaboratorForm>(FORM_EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [confirmDelete, setConfirmDelete] = useState<Collaborator | null>(null);
  // «No hay colaboradores todavía» es una afirmación con consecuencia: desde
  // aquí se da de alta uno, y dar de alta el que ya existe deja dos logos
  // iguales en la web pública.
  const [errorCarga, setErrorCarga] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const { data, error: errorLectura } = await supabase
      .from('collaborators')
      .select('id, name, logo_url, url, sort_order, active')
      .order('sort_order')
      .order('name');
    if (errorLectura) {
      setErrorCarga(errorLectura.message || 'Error desconocido');
      setCollaborators([]);
      setLoading(false);
      return;
    }
    setErrorCarga(null);
    setCollaborators((data as Collaborator[]) ?? []);
    setLoading(false);
  }

  function openCreate() {
    setEditTarget(null);
    setForm(FORM_EMPTY);
    setUploadError('');
    setSaveError('');
    setModal(true);
  }
  function openEdit(c: Collaborator) {
    setEditTarget(c);
    setForm({
      name: c.name,
      url: c.url,
      logo_url: c.logo_url,
      sort_order: c.sort_order,
      active: c.active,
    });
    setUploadError('');
    setSaveError('');
    setModal(true);
  }

  async function uploadLogo(file: File) {
    const ext = file.name.split('.').pop();
    const path = `collaborators/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    setUploadError('');
    setUploading(true);
    const { error } = await supabase.storage.from('vertex-assets').upload(path, file);
    if (error) {
      setUploadError(
        `Error al subir "${file.name}": ${error.message}. Verifica que el bucket "vertex-assets" existe y es público.`
      );
      setUploading(false);
      return;
    }
    const { data } = supabase.storage.from('vertex-assets').getPublicUrl(path);
    setForm((f) => ({ ...f, logo_url: data.publicUrl }));
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) uploadLogo(file);
  }

  async function handleSave() {
    setSaveError('');
    const name = form.name.trim();
    const url = form.url.trim();
    if (!name) return setSaveError('El nombre es obligatorio.');
    if (!form.logo_url) return setSaveError('El logo es obligatorio.');
    if (!url || !url.startsWith('https://') || url === 'https://') {
      return setSaveError('La URL es obligatoria y debe empezar por https://');
    }

    setSaving(true);
    const payload = {
      name,
      url,
      logo_url: form.logo_url,
      sort_order: form.sort_order,
      active: form.active,
    };
    const res = editTarget
      ? await supabase.from('collaborators').update(payload).eq('id', editTarget.id)
      : await supabase.from('collaborators').insert(payload);
    setSaving(false);
    if (res.error) {
      setSaveError(`No se pudo guardar: ${res.error.message}`);
      return;
    }
    await load();
    setModal(false);
  }

  async function toggleCollaborator(c: Collaborator) {
    const { error } = await supabase
      .from('collaborators')
      .update({ active: !c.active })
      .eq('id', c.id);
    // El error se comprueba ANTES de tocar el estado: pintar la fila como
    // desactivada cuando la base ha dicho que no es peor que no pintar nada,
    // porque el cambio falso sobrevive hasta la siguiente recarga.
    if (error) {
      alert(`No se pudo ${c.active ? 'desactivar' : 'activar'}: ${error.message}`);
      return;
    }
    setCollaborators((prev) => prev.map((x) => (x.id === c.id ? { ...x, active: !x.active } : x)));
  }

  async function deleteCollaborator(c: Collaborator) {
    const { error } = await supabase.from('collaborators').delete().eq('id', c.id);
    if (error) {
      alert(`No se pudo eliminar: ${error.message}`);
      return;
    }
    setCollaborators((prev) => prev.filter((x) => x.id !== c.id));
    setConfirmDelete(null);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-oswald text-2xl text-white uppercase tracking-wide">Colaboradores</h1>
          <p className="text-text-secondary text-sm">
            Se muestran al alumno en su pantalla de inicio (solo el logo), debajo de Patrocinadores,
            ordenados por «Orden».
          </p>
        </div>
        <button
          onClick={openCreate}
          className="btn-gold flex items-center gap-2"
          data-testid="collaborator-new"
        >
          <Plus size={16} /> Nuevo colaborador
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center h-48 items-center">
          <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
        </div>
      ) : errorCarga ? (
        <div className="card text-center py-12" data-testid="colaboradores-ilegibles">
          <p className="text-error text-sm">{errorCarga}</p>
          <p className="text-text-secondary text-sm mt-2">
            No dé la lista por vacía: crear uno ahora duplicaría el que ya esté dado de alta, y en
            la web pública se verían los dos logos.
          </p>
          <button
            className="btn-secondary mt-4"
            onClick={load}
            data-testid="colaboradores-reintentar"
          >
            Reintentar
          </button>
        </div>
      ) : collaborators.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-text-secondary text-sm">No hay colaboradores todavía.</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-secondary border-b border-bg-border">
                <th className="p-3 font-inter font-normal">Logo</th>
                <th className="p-3 font-inter font-normal">Nombre</th>
                <th className="p-3 font-inter font-normal">URL</th>
                <th className="p-3 font-inter font-normal w-20">Orden</th>
                <th className="p-3 font-inter font-normal w-24">Estado</th>
                <th className="p-3 font-inter font-normal w-28 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {collaborators.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`collaborator-row-${c.id}`}
                  className={`border-b border-bg-border/60 ${!c.active ? 'opacity-50' : ''}`}
                >
                  <td className="p-3">
                    <div className="w-12 h-12 rounded bg-bg-elevated flex items-center justify-center overflow-hidden">
                      {c.logo_url ? (
                        <img
                          src={c.logo_url}
                          alt={c.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <ImageIcon size={16} className="text-bg-border" />
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-white font-medium">{c.name}</td>
                  <td className="p-3">
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-gold-primary hover:underline inline-flex items-center gap-1"
                    >
                      <span className="truncate max-w-[200px] inline-block align-bottom">
                        {c.url}
                      </span>
                      <ExternalLink size={12} />
                    </a>
                  </td>
                  <td className="p-3 text-text-secondary">{c.sort_order}</td>
                  <td className="p-3">
                    <span className={c.active ? 'text-success' : 'text-text-secondary'}>
                      {c.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => openEdit(c)}
                        title="Editar"
                        data-testid={`collaborator-edit-${c.id}`}
                        className="w-7 h-7 rounded flex items-center justify-center text-text-secondary hover:text-gold-primary"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => toggleCollaborator(c)}
                        title={c.active ? 'Desactivar' : 'Activar'}
                        className={`w-7 h-7 rounded flex items-center justify-center text-text-secondary ${c.active ? 'hover:text-warning' : 'hover:text-success'}`}
                      >
                        <Power size={14} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(c)}
                        title="Eliminar"
                        data-testid={`collaborator-delete-${c.id}`}
                        className="w-7 h-7 rounded flex items-center justify-center text-text-secondary hover:text-error"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Confirm
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteCollaborator(confirmDelete)}
        title="Eliminar colaborador"
        message={`¿Borrar "${confirmDelete?.name}" definitivamente? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        danger
      />

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editTarget ? 'Editar colaborador' : 'Nuevo colaborador'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="label">Nombre * (uso interno, no se muestra en la app)</label>
            <input
              className="input"
              data-testid="collaborator-form-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">URL (https://) *</label>
            <input
              className="input"
              data-testid="collaborator-form-url"
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              placeholder="https://colaborador.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Orden</label>
              <input
                type="number"
                className="input"
                data-testid="collaborator-form-sort"
                value={form.sort_order}
                onChange={(e) => setForm((f) => ({ ...f, sort_order: +e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Estado</label>
              <label className="flex items-center gap-2 h-[42px] text-sm text-text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="collaborator-form-active"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
                Activo (visible para el alumno)
              </label>
            </div>
          </div>

          <div>
            <label className="label">Logo *</label>
            {uploadError && (
              <p className="text-error text-xs bg-error/10 rounded p-2 mb-2">{uploadError}</p>
            )}
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 rounded bg-bg-elevated flex items-center justify-center overflow-hidden border border-bg-border">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="logo" className="w-full h-full object-contain" />
                ) : (
                  <ImageIcon size={20} className="text-bg-border" />
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="collaborator-form-logo-input"
                onChange={handleFileChange}
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="btn-ghost flex items-center gap-2 text-sm"
              >
                <ImageIcon size={14} /> {uploading ? 'Subiendo...' : 'Subir logo'}
              </button>
            </div>
          </div>

          {saveError && (
            <p
              className="text-error text-xs bg-error/10 rounded p-2"
              data-testid="collaborator-save-error"
            >
              {saveError}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button onClick={() => setModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-gold"
              data-testid="collaborator-save"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
